/* External */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useReducer,
} from 'react';
import { useLocation } from 'react-router-dom';

/* Utils */
import { MY_LIST_PARAMS, PROVIDERS } from '../utils/constants';
import useFetchCarousel from './useFetchCarousel';
import useContent from './useContent';
import backendService from '../services/backendService';
import { isLearnActionV4Active } from '../utils/env';
import { useUserAccount } from '../providers/UserProvider';
import { PLAY_SOURCE_PAGE } from '../utils/trackSourceConviva';

export default function useAddRemoveWatchList({
  contentId, vmsId, type, vrioAssetId, sourceProvider = PROVIDERS.VRIO,
}) {
  const { state: locationState } = useLocation();

  const { actionOrigin: locationActionOrigin } = locationState || {};

  const {
    msoProvider,
    accountType: userType,
  } = useUserAccount() || {};

  const [reloader, reloadMyList] = useReducer((s) => s + 1, 0);
  const content = useContent(contentId, sourceProvider);

  const { carousel: { contents: items } = {} } = useFetchCarousel({
    sourceCarouselId: MY_LIST_PARAMS.SOURCE_CAROUSEL_ID,
    type: MY_LIST_PARAMS.TYPE,
    source: MY_LIST_PARAMS.SOURCE,
    sourceProvider,
    reloader,
  });

  const foundIndex = useMemo(
    () => (items ? items.findIndex((item) => item.id === `${contentId}-${sourceProvider}`) : -1),
    [contentId, items, sourceProvider],
  );

  const found = foundIndex >= 0;
  const [isAddedWatchList, setIsAddedWatchList] = useState(found);

  useEffect(() => {
    reloadMyList();
  }, [contentId]);

  useEffect(() => {
    setIsAddedWatchList(found);
  }, [found]);

  const params = useMemo(() => ({
    id: vmsId || contentId,
    type,
    actionOrigin: content?.actionOrigin || locationActionOrigin || '',
    userOrigin: {
      playSource: PLAY_SOURCE_PAGE.PDP,
      userLogin: userType,
      msoProvider,
    },
    ...(isLearnActionV4Active && { vrioAssetId }),
  }), [
    contentId,
    type,
    vmsId,
    vrioAssetId,
    content,
    msoProvider,
    userType,
    locationActionOrigin,
  ]);

  const fetchAddToMyList = useCallback((paramsAddToMyList) => (isLearnActionV4Active
    ? backendService.experience.v4.addToMyList(paramsAddToMyList)
    : backendService.experience.v3.addToMyList(paramsAddToMyList)), []);

  const handleAddRemoveWatchList = useCallback(async () => {
    try {
      await (
        isAddedWatchList
          ? backendService.experience.v3.removeFromMyList(params)
          : fetchAddToMyList(params)
      );
    } catch (error) {
      console.error('handleAddRemoveWatchList Error:', error);
    }
    setIsAddedWatchList(!isAddedWatchList);
    reloadMyList();
  }, [isAddedWatchList, params, fetchAddToMyList]);

  return {
    isAddedWatchList,
    handleAddRemoveWatchList,
  };
}
